import React, { useRef } from 'react';
import { chartElementAtEvent } from '../chartInteraction';
import type { Chart as ChartJS } from 'chart.js';
import { Line } from 'react-chartjs-2';
import KpiChartPanel from './KpiChartPanel';
import { formatAxisCompactK, formatDataLabelCompact } from '../chartFormatters';

export interface TrendSeries {
  label: string;
  data: number[];
  borderColor: string;
  backgroundColor: string;
}

export interface KpiMonthlyTrendChartProps {
  title: string;
  series: TrendSeries[];
  /** One label per month bucket (same length as each `series[].data`), e.g. `Jan 25` */
  monthLabels: string[];
  onPointClick?: (monthIndex: number, seriesLabel: 'Case' | 'Incident' | 'Request') => void;
}

const TREND_LABELS = ['Case', 'Incident', 'Request'] as const;

const KpiMonthlyTrendChart: React.FC<KpiMonthlyTrendChartProps> = ({ title, series, monthLabels, onPointClick }) => {
  const chartRef = useRef<ChartJS<'line'>>(null);
  const maxY = Math.max(0, ...series.flatMap((s) => s.data));
  const pointCount = series[0]?.data.length ?? 12;
  const labels =
    monthLabels.length >= pointCount ? monthLabels.slice(0, pointCount) : [...monthLabels, ...Array(pointCount - monthLabels.length).fill('')];
  const showPointValueLabels = pointCount <= 24;

  const handleClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onPointClick || !chartRef.current) return;
    const els = chartElementAtEvent(chartRef.current, event);
    const el = els[0];
    if (el == null) return;
    const seriesLabel = TREND_LABELS[el.datasetIndex];
    if (!seriesLabel) return;
    onPointClick(el.index, seriesLabel);
  };

  return (
    <KpiChartPanel title={title} className="h-full lg:col-span-1" flexFill>
      <div className="relative h-[350px] w-full shrink-0">
        <Line
          ref={chartRef}
          onClick={onPointClick ? handleClick : undefined}
          data={{
            labels,
            datasets: series.map((s, dsi) => ({
              label: s.label,
              data: s.data,
              borderColor: s.borderColor,
              backgroundColor: s.backgroundColor,
              fill: true,
              tension: 0.35,
              pointRadius: 3,
              pointHoverRadius: 5,
              pointBackgroundColor: '#fff',
              pointBorderWidth: 2,
              borderWidth: 2,
              datalabels: {
                display: showPointValueLabels,
                anchor: 'end',
                align: 'end',
                offset: 4 + dsi * 10,
                clip: false,
                color: s.borderColor as string,
                font: { size: 9, weight: 600 },
                formatter: (value: number) => formatDataLabelCompact(Number(value)),
              },
            })),
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: { top: 0, right: 8, bottom: 0 } },
            onHover: (event, elements) => {
              if (event.native?.target instanceof HTMLElement) {
                event.native.target.style.cursor =
                  onPointClick && elements.length > 0 ? 'pointer' : 'default';
              }
            },
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: {
                position: 'bottom',
                align: 'center',
                labels: {
                  boxWidth: 10,
                  boxHeight: 10,
                  padding: 16,
                  usePointStyle: true,
                  color: '#475569',
                  font: { size: 12, weight: 500 },
                },
              },
            },
            scales: {
              x: {
                grid: { display: false },
                ticks: {
                  color: '#64748b',
                  maxRotation: labels.length > 12 ? 50 : 0,
                  autoSkip: true,
                  maxTicksLimit: labels.length > 24 ? 18 : 24,
                },
              },
              y: {
                display: true,
                beginAtZero: true,
                suggestedMax: maxY > 0 ? maxY * 1.12 : undefined,
                grid: { color: 'rgba(148, 163, 184, 0.12)' },
                border: { display: false },
                ticks: {
                  color: '#94a3b8',
                  maxTicksLimit: 6,
                  callback: (v) => formatAxisCompactK(v as number),
                },
              },
            },
          }}
        />
      </div>
    </KpiChartPanel>
  );
};

export default KpiMonthlyTrendChart;
